import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import * as styles from '@/components/interAction/Drag/DragStyle.css';

type DraggableWordProps = {
  id: string;
  label?: string;
  color?: string; // 단어 맞추기용 정답 색상
  currentCategory?: string; // 다이어그램용
  initialWords?: { id: string; category: string }[]; // 다이어그램용
  className?: string;
  readOnly?: boolean; // 읽기 모드 (드래그 비활성화)
  isHomework?: boolean; // 숙제 여부 (정답 색상 표시 안 함)
};

export default function DraggableWord({
  id,
  label,
  color,
  currentCategory,
  initialWords,
  className = '',
  readOnly = false,
  isHomework = false,
}: DraggableWordProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const [isHovered, setIsHovered] = useState(false);

  const getClassNameByColor = () => {
    // 숙제인 경우 항상 기본 색상
    if (isHomework) {
      return styles.wordDefault;
    }

    // 1. color가 명시된 경우 (단어 맞추기)
    if (color) {
      switch (color) {
        case '#1D9F73':
          return styles.wordCorrect;
        case '#F95567':
          return styles.wordWrong;
        default:
          return styles.wordDefault;
      }
    }

    // 2. currentCategory + initialWords 가 주어진 경우 (카테고리 매칭)
    if (currentCategory && initialWords) {
      const correctCategory = initialWords.find((w) => w.id === id)?.category;
      if (currentCategory === 'Pool') {
        return styles.wordDefault;
      }
      return correctCategory === currentCategory ? styles.wordCorrect : styles.wordWrong;
    }

    // 3. 기본
    return styles.wordDefault;
  };

  const sanitizeLabel = (text: string) => text?.replace(/-\d+-\d+$/, '') ?? '';
  const displayLabel = sanitizeLabel(label ?? id);

  return (
    <div
      ref={setNodeRef}
      {...(readOnly ? {} : listeners)}
      {...(readOnly ? {} : attributes)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(getClassNameByColor(), className, readOnly && 'cursor-default')}
      style={{
        transform: `${transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : ''}${isHovered && !readOnly ? ' translateY(-3px)' : ''}`.trim(),
        opacity: isDragging ? 0.5 : 1,
        touchAction: readOnly ? 'auto' : 'none',
        zIndex: 100
      }}
    >
      {displayLabel}
    </div>
  );
}